import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "../../lib/supabase";
import GalleryCard from "../../components/GalleryCard";
import toast from "react-hot-toast";
import { useViewportAnimationTrigger } from "../../hooks/useViewportAnimationTrigger";
import { m } from "framer-motion";
import { pageFade } from "../../components/animations/menuAnimations";
import { EFFECT_OPTIONS, parseEffects } from "../../utils/effects";
import { logger } from "../../utils/logger";
import ConfirmationModal from "../../components/ui/ConfirmationModal";

// Interface matching the actual gallery_cards table schema
interface GalleryCardData {
  id: string;
  sort_order: number | null;
  image_url: string | null;
  effect: string | null;
  updated_at: string | null;
  created_at: string | null;
  is_active: boolean | null;
  title: string;
  description: string | null;
}

const AdminGallery = () => {
  const containerRef = useViewportAnimationTrigger();
  const [galleryCards, setGalleryCards] = useState<GalleryCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);

  // Check admin status
  const checkAdminStatus = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setError("You must be logged in to access this page");
        setCheckingAdmin(false);
        return;
      }

      const { data: customer, error: customerError } = await supabase
        .from("customers")
        .select("is_admin")
        .eq("id", user.id)
        .single();

      if (customerError) {
        logger.error("Error checking admin status:", customerError);
        setError("Failed to verify admin status");
        setCheckingAdmin(false);
        return;
      }

      if (!customer || !(customer as { is_admin?: boolean }).is_admin) {
        setError("Access denied. Admin privileges required.");
        setCheckingAdmin(false);
        return;
      }

      setIsAdmin(true);
      setCheckingAdmin(false);
    } catch (err) {
      logger.error("Error checking admin status:", err);
      setError("An error occurred while verifying permissions");
      setCheckingAdmin(false);
    }
  };

  const fetchGalleryCards = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("gallery_cards")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;

      setGalleryCards((data || []) as GalleryCardData[]);
    } catch (error) {
      logger.error("Error fetching gallery cards:", error);
      toast.error("Failed to load gallery cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    fetchGalleryCards();

    const channel = supabase
      .channel("gallery-cards-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "gallery_cards" },
        () => fetchGalleryCards(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const toggleActive = async (
    cardId: string,
    currentStatus: boolean | null,
  ) => {
    const { error } = await supabase
      .from("gallery_cards")
      .update({
        is_active: !currentStatus,
        updated_at: new Date().toISOString(),
      })
      .eq("id", cardId);

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success(currentStatus ? "Card hidden" : "Card activated");
    }
  };

  const openDeleteConfirm = (cardId: string) => {
    setCardToDelete(cardId);
    setShowDeleteConfirm(true);
  };

  const deleteCard = async () => {
    if (!cardToDelete) return;

    const { error } = await supabase
      .from("gallery_cards")
      .delete()
      .eq("id", cardToDelete);

    if (error) {
      toast.error("Failed to delete card");
    } else {
      toast.success("Card deleted");
      setShowDeleteConfirm(false);
      setCardToDelete(null);
    }
  };

  const addNewCard = async () => {
    const maxSortOrder = Math.max(
      ...galleryCards.map((c) => c.sort_order || 0),
      0,
    );

    const { error } = await supabase.from("gallery_cards").insert({
      sort_order: maxSortOrder + 1,
      image_url:
        "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80",
      effect: "crossfade",
      is_active: true,
      title: "New Gallery Card",
      description: "Add a description for this card",
    });

    if (error) {
      toast.error("Failed to add card");
    } else {
      toast.success("New card added");
    }
  };

  if (checkingAdmin) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center bg-[var(--bg-main)] p-8">
        <div className="text-[var(--text-muted)]">Verifying permissions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center bg-[var(--bg-main)] p-8">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <m.main
      ref={containerRef}
      className="w-full bg-[var(--bg-main)] text-[var(--text-main)]"
      variants={pageFade}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{ pointerEvents: "auto" }}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-3 sm:py-4 md:py-5">
        <header className="mb-8 flex flex-col gap-3 sm:gap-4 md:gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-main)]">
              Gallery Management
            </h1>
            <p className="mt-2 text-sm sm:text-base text-[var(--text-muted)]">
              Manage gallery cards and their effects
            </p>
          </div>
          <button
            onClick={addNewCard}
            className="px-6 py-3 rounded-xl bg-[var(--accent)] text-white font-semibold hover:opacity-90 transition-all min-h-[44px]"
          >
            + Add New Card
          </button>
        </header>

        {loading ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            Loading gallery cards...
          </div>
        ) : galleryCards.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-theme bg-theme-elevated">
            <div className="max-w-md mx-auto space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center border border-[var(--accent)]/30 text-[var(--accent)]">
                <svg
                  className="w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[var(--text-main)]">
                No Gallery Cards
              </h3>
              <p className="text-[var(--text-muted)]">
                Add your first gallery card to get started.
              </p>
              <button
                onClick={addNewCard}
                className="px-6 py-3 rounded-xl bg-[var(--accent)] text-white font-semibold hover:opacity-90 transition-all"
              >
                Add First Card
              </button>
            </div>
          </div>
        ) : (
          <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {galleryCards.map((card, index) => {
              const baseEffects = parseEffects(card.effect);

              return (
                <article
                  key={card.id}
                  className={`flex flex-col overflow-hidden rounded-2xl border border-theme bg-theme-elevated shadow-lg ${
                    card.is_active ? "" : "opacity-70"
                  }`}
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <div className="p-6 space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-[var(--text-main)]">
                          {card.title}
                        </h3>
                        <p className="text-xs text-[var(--text-muted)]">
                          Sort Order: {card.sort_order || 0}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase ${
                          card.is_active
                            ? "border-emerald-500/40 text-emerald-300"
                            : "border-rose-500/40 text-rose-300"
                        }`}
                      >
                        {card.is_active ? "Active" : "Hidden"}
                      </span>
                    </div>

                    {/* Image Preview */}
                    <div className="aspect-[4/3] w-full overflow-hidden rounded-xl border border-theme bg-[rgba(10,12,18,0.92)]">
                      <GalleryCard
                        defaultImage={card.image_url || ""}
                        hoverImage={card.image_url || ""}
                        effect={baseEffects || "crossfade"}
                        alt={card.title}
                        caption={card.description || card.title}
                      />
                    </div>

                    {/* Effect Info */}
                    <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                      <span>
                        Effect:{" "}
                        <span className="font-semibold text-[var(--accent)]">
                          {EFFECT_OPTIONS.find((o) =>
                            baseEffects.includes(o.value),
                          )?.label ||
                            baseEffects[0] ||
                            "Crossfade"}
                        </span>
                      </span>
                      <span>
                        Updated{" "}
                        {formatDistanceToNow(
                          new Date(
                            card.updated_at ||
                              card.created_at ||
                              new Date().toISOString(),
                          ),
                          { addSuffix: true },
                        )}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => toggleActive(card.id, card.is_active)}
                        className={`flex-1 rounded-lg border px-4 py-3 text-xs font-semibold uppercase transition-all ${
                          card.is_active
                            ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                            : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        }`}
                      >
                        {card.is_active ? "Hide Card" : "Activate Card"}
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(card.id)}
                        className="rounded-lg border border-red-500/40 text-red-300 px-4 py-3 text-xs font-semibold uppercase hover:bg-red-500/10 transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}

        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setCardToDelete(null);
          }}
          onConfirm={deleteCard}
          title="Delete Gallery Card"
          message="Are you sure you want to delete this gallery card? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    </m.main>
  );
};

export default AdminGallery;
